import React, { useEffect, useState } from "react";

import dayjs from "dayjs";
import { CalendarIcon } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { Check } from "~/hooks/useChecks";
import { useUpdateCheckDates } from "~/hooks/useUpdateCheckDates";

interface EditCheckDatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  check: Check | null;
  onSuccess?: () => void;
}

const EditCheckDatesDialog: React.FC<EditCheckDatesDialogProps> = ({
  open,
  onOpenChange,
  check,
  onSuccess,
}) => {
  const [receptionDate, setReceptionDate] = useState<Date | undefined>();
  const [collectionDate, setCollectionDate] = useState<Date | undefined>();
  const { mutate: updateDates, isPending } = useUpdateCheckDates();

  useEffect(() => {
    if (check && open) {
      setReceptionDate(
        check.reception_date ? dayjs(check.reception_date).toDate() : undefined
      );
      setCollectionDate(
        check.collection_date
          ? dayjs(check.collection_date).toDate()
          : undefined
      );
    }
  }, [check, open]);

  const handleSubmit = () => {
    if (!check) return;

    updateDates(
      {
        checkId: check.id,
        receptionDate: receptionDate ? receptionDate.toISOString() : null,
        collectionDate: collectionDate ? collectionDate.toISOString() : null,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          onSuccess?.();
        },
      }
    );
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return "Seleccionar fecha";
    return dayjs(date).format("DD/MM/YYYY");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Editar Fechas del Cheque</DialogTitle>
          <DialogDescription>
            Modifique las fechas de recepcion y cobro del cheque
            {check?.check_number && ` N° ${check.check_number}`}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label className="mb-2 block">Fecha de Recepcion</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`w-full justify-start text-left font-normal ${
                    !receptionDate && "text-muted-foreground"
                  }`}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formatDate(receptionDate)}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={receptionDate}
                  onSelect={setReceptionDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label className="mb-2 block">Fecha de Cobro</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`w-full justify-start text-left font-normal ${
                    !collectionDate && "text-muted-foreground"
                  }`}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formatDate(collectionDate)}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={collectionDate}
                  onSelect={setCollectionDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditCheckDatesDialog;
